import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { theme, alpha } from '../../../theme';
import { useVenueForm } from '../../../hooks/venue/useVenueForm';
import { useVenueDetail } from '../../../hooks/venue/useVenueDetail';
import { VenueType } from '../../../types/venue';
import VenuePhotoManager from '../../../components/venue/owner/VenuePhotoManager';
import {
  ArrowLeft,
  Save,
  X,
  DollarSign,
  Users,
  Home,
  Lightbulb,
  Car,
  Droplet,
  ShieldCheck,
  Image as ImageIcon,
  Calendar,
} from 'lucide-react';

const VENUE_TYPES: { value: VenueType; label: string }[] = [
  { value: 'FUTBOL_5', label: 'Fútbol 5' },
  { value: 'FUTBOL_6', label: 'Fútbol 6' },
  { value: 'FUTBOL_7', label: 'Fútbol 7' },
  { value: 'FUTBOL_8', label: 'Fútbol 8' },
  { value: 'FUTBOL_9', label: 'Fútbol 9' },
  { value: 'FUTBOL_10', label: 'Fútbol 10' },
  { value: 'FUTBOL_11', label: 'Fútbol 11' },
];

const SURFACE_TYPES = [
  'Césped sintético',
  'Césped natural',
  'Cemento',
  'Parquet',
  'Polvo de ladrillo',
];

const CreateVenuePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  // For edit mode, fetch existing venue
  const { venue, loading: loadingVenue, fetchVenue } = useVenueDetail(id || '');
  const { formData, errors, loading, updateField, submitForm } = useVenueForm(venue || undefined);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const createdVenueRef = useRef<any>(null);
  const [venuePhotos, setVenuePhotos] = useState<any[]>([]);

  // Auto-create venue before photo upload if not yet created
  const ensureVenueCreated = async (): Promise<string | null> => {
    if (isEditing && venue) return venue.id;
    if (createdVenueRef.current) return createdVenueRef.current.id;

    const result = await submitForm();
    if (result.success && result.venue) {
      createdVenueRef.current = result.venue;
      return result.venue.id;
    } else {
      setSubmitError(result.error || 'Error al crear la cancha. Completá los campos requeridos.');
      return null;
    }
  };

  useEffect(() => {
    if (id) {
      fetchVenue();
    }
  }, [id, fetchVenue]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const result = await submitForm();

    if (result.success && result.venue) {
      if (isEditing) {
        setSuccessMessage('Cancha actualizada correctamente');
        fetchVenue();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setSuccessMessage('Cancha creada. Ahora podés subir fotos.');
        createdVenueRef.current = result.venue;
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } else {
      // eslint-disable-next-line no-console
      console.error('❌ [CreateVenuePage] Error al guardar:', result.error);
      setSubmitError(result.error || 'Error al guardar el venue');
    }
  };

  const handleCancel = () => {
    navigate('/owner/dashboard');
  };

  if (isEditing && loadingVenue) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingTextStyle}>Cargando cancha...</div>
      </div>
    );
  }

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const breadcrumbStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const breadcrumbLinkStyle: React.CSSProperties = {
    color: theme.colors.primary,
    cursor: 'pointer',
    textDecoration: 'none',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const formContainerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 400px',
    gap: '32px',
    alignItems: 'start',
  };

  const formStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '32px',
    border: `2px solid ${theme.colors.border}`,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '16px',
    fontFamily: theme.fonts.heading,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const inputGroupStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
  };

  const inputErrorStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: theme.colors.danger,
  };

  const errorMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.danger,
    marginTop: '4px',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical',
    fontFamily: 'inherit',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  };

  const checkboxItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.input,
    cursor: 'pointer',
    border: `2px solid ${theme.colors.border}`,
    transition: 'all 0.2s',
    color: theme.colors.text,
  };

  const checkboxItemActiveStyle: React.CSSProperties = {
    ...checkboxItemStyle,
    borderColor: theme.colors.primary,
    backgroundColor: `${alpha('primary', '15')}`,
    color: theme.colors.primary,
  };

  const checkboxInputStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  };

  const sidebarStyle: React.CSSProperties = {
    position: 'sticky',
    top: '32px',
  };

  const guideCardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '20px',
    border: `2px solid ${theme.colors.border}`,
    marginBottom: '20px',
  };

  const guideItemStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
    lineHeight: '1.5',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: `2px solid ${theme.colors.border}`,
  };

  const buttonBaseStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
  };

  const submitButtonStyle: React.CSSProperties = {
    ...buttonBaseStyle,
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const submitButtonDisabledStyle: React.CSSProperties = {
    ...submitButtonStyle,
    opacity: 0.5,
    cursor: 'not-allowed',
  };

  const alertStyle: React.CSSProperties = {
    padding: '12px',
    backgroundColor: `${alpha('danger', '20')}`,
    border: `2px solid ${theme.colors.danger}`,
    borderRadius: theme.borderRadius.input,
    color: theme.colors.danger,
    marginBottom: '20px',
  };

  const gridTwoColStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  };

  const toastStyle: React.CSSProperties = {
    position: 'fixed',
    top: '24px',
    right: '24px',
    padding: '16px 24px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    borderRadius: theme.borderRadius.input,
    fontWeight: theme.fonts.weightBold,
    fontSize: theme.fonts.sizeBody,
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
    animation: 'slideIn 0.3s ease-out',
  };

  return (
    <div style={containerStyle}>
      {successMessage && <div style={toastStyle}>{successMessage}</div>}
      {/* Header */}
      <div style={headerStyle}>
        <div style={breadcrumbStyle}>
          <a
            style={breadcrumbLinkStyle}
            onClick={() => navigate('/owner/dashboard')}
          >
            Dashboard
          </a>
          <span>/</span>
          <span>{isEditing ? 'Editar cancha' : 'Crear cancha'}</span>
        </div>
        <h1 style={titleStyle}>
          {isEditing ? 'EDITAR CANCHA' : 'CREAR NUEVA CANCHA'}
        </h1>
        <p style={subtitleStyle}>
          {isEditing
            ? 'Modificá la información de tu cancha'
            : 'Completá la información para publicar tu cancha'}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} onDragOver={(e) => e.preventDefault()} onDrop={(e) => e.preventDefault()}>
        <div style={formContainerStyle}>
          {/* Left Column - Form */}
          <div style={formStyle}>
            {submitError && <div style={alertStyle}>{submitError}</div>}

            {/* Section 1: Basic Info */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>
                <Home size={24} />
                Información Básica
              </h2>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="name">
                  Nombre de la cancha *
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  placeholder="Ej: Complejo Deportivo La Bombonera"
                  style={errors.name ? inputErrorStyle : inputStyle}
                />
                {errors.name && (
                  <div style={errorMessageStyle}>{errors.name}</div>
                )}
              </div>

              {/* complexId is now auto-assigned by backend from owner's complex */}

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="venueType">
                  Tipo de cancha *
                </label>
                <select
                  id="venueType"
                  value={formData.venueType}
                  onChange={(e) =>
                    updateField('venueType', e.target.value as VenueType)
                  }
                  style={selectStyle}
                >
                  {VENUE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Section 2: Amenities */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>
                <ShieldCheck size={24} />
                Características
              </h2>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="surfaceType">
                  Tipo de superficie *
                </label>
                <select
                  id="surfaceType"
                  value={formData.surfaceType}
                  onChange={(e) => updateField('surfaceType', e.target.value)}
                  style={errors.surfaceType ? inputErrorStyle : selectStyle}
                >
                  {SURFACE_TYPES.map((surface) => (
                    <option key={surface} value={surface}>
                      {surface}
                    </option>
                  ))}
                </select>
                {errors.surfaceType && (
                  <div style={errorMessageStyle}>{errors.surfaceType}</div>
                )}
              </div>

              <label
                style={{ ...labelStyle, marginBottom: '12px', display: 'block' }}
              >
                Comodidades
              </label>
              <div style={checkboxContainerStyle}>
                <label
                  style={
                    formData.isIndoor
                      ? checkboxItemActiveStyle
                      : checkboxItemStyle
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.isIndoor}
                    onChange={(e) => updateField('isIndoor', e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  <Home size={18} />
                  <span>Cubierta</span>
                </label>

                <label
                  style={
                    formData.hasLighting
                      ? checkboxItemActiveStyle
                      : checkboxItemStyle
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.hasLighting}
                    onChange={(e) =>
                      updateField('hasLighting', e.target.checked)
                    }
                    style={checkboxInputStyle}
                  />
                  <Lightbulb size={18} />
                  <span>Iluminación</span>
                </label>

                <label
                  style={
                    formData.hasParking
                      ? checkboxItemActiveStyle
                      : checkboxItemStyle
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.hasParking}
                    onChange={(e) => updateField('hasParking', e.target.checked)}
                    style={checkboxInputStyle}
                  />
                  <Car size={18} />
                  <span>Estacionamiento</span>
                </label>

                <label
                  style={
                    formData.hasBathrooms
                      ? checkboxItemActiveStyle
                      : checkboxItemStyle
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.hasBathrooms}
                    onChange={(e) =>
                      updateField('hasBathrooms', e.target.checked)
                    }
                    style={checkboxInputStyle}
                  />
                  <Droplet size={18} />
                  <span>Baños</span>
                </label>

                <label
                  style={
                    formData.hasChangingRooms
                      ? checkboxItemActiveStyle
                      : checkboxItemStyle
                  }
                >
                  <input
                    type="checkbox"
                    checked={formData.hasChangingRooms}
                    onChange={(e) =>
                      updateField('hasChangingRooms', e.target.checked)
                    }
                    style={checkboxInputStyle}
                  />
                  <Users size={18} />
                  <span>Vestuarios</span>
                </label>
              </div>
            </div>

            {/* Section: Photos */}
            <div id="photos-section" style={sectionStyle}>
              <h2 style={sectionTitleStyle}>
                <ImageIcon size={24} />
                Fotos
              </h2>
              <VenuePhotoManager
                venueId={isEditing ? venue?.id : createdVenueRef.current?.id}
                photos={isEditing ? (venue?.photos || []) : venuePhotos}
                onPhotosChange={(photos) => {
                  if (isEditing) {
                    fetchVenue();
                  } else {
                    setVenuePhotos(photos);
                  }
                }}
                ensureVenueCreated={!isEditing ? ensureVenueCreated : undefined}
              />
            </div>

            {/* Section 5: Pricing */}
            <div style={sectionStyle}>
              <h2 style={sectionTitleStyle}>
                <DollarSign size={24} />
                Precios
              </h2>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="hourlyRate">
                  Precio por hora (normal) *
                </label>
                <input
                  id="hourlyRate"
                  type="number"
                  min="0"
                  step="100"
                  value={formData.hourlyRate}
                  onChange={(e) =>
                    updateField('hourlyRate', parseFloat(e.target.value) || 0)
                  }
                  placeholder="Ej: 5000"
                  style={errors.hourlyRate ? inputErrorStyle : inputStyle}
                />
                {errors.hourlyRate && (
                  <div style={errorMessageStyle}>{errors.hourlyRate}</div>
                )}
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="peakHourRate">
                  Precio por hora (hora pico) - Opcional
                </label>
                <input
                  id="peakHourRate"
                  type="number"
                  min="0"
                  step="100"
                  value={formData.peakHourRate}
                  onChange={(e) =>
                    updateField('peakHourRate', parseFloat(e.target.value) || 0)
                  }
                  placeholder="Ej: 7000"
                  style={errors.peakHourRate ? inputErrorStyle : inputStyle}
                />
                {errors.peakHourRate && (
                  <div style={errorMessageStyle}>{errors.peakHourRate}</div>
                )}
                <div style={{ ...guideItemStyle, marginTop: '4px' }}>
                  Generalmente fines de semana o horarios de mayor demanda
                </div>
              </div>

              <div style={inputGroupStyle}>
                <label style={labelStyle} htmlFor="depositAmount">
                  Monto de seña/depósito *
                </label>
                <input
                  id="depositAmount"
                  type="number"
                  min="0"
                  step="100"
                  value={formData.depositAmount}
                  onChange={(e) =>
                    updateField('depositAmount', parseFloat(e.target.value) || 0)
                  }
                  placeholder="Ej: 2000"
                  style={errors.depositAmount ? inputErrorStyle : inputStyle}
                />
                {errors.depositAmount && (
                  <div style={errorMessageStyle}>{errors.depositAmount}</div>
                )}
                <div style={{ ...guideItemStyle, marginTop: '4px' }}>
                  Monto que deben pagar los usuarios al confirmar la reserva
                </div>
              </div>
            </div>

            {/* Footer Buttons */}
            <div style={footerStyle}>
              <button
                type="button"
                style={cancelButtonStyle}
                onClick={handleCancel}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.border;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme.colors.background;
                }}
              >
                <X size={20} />
                Cancelar
              </button>

              <div style={{ display: 'flex', gap: 12, flex: 1, justifyContent: 'flex-end' }}>
                {isEditing && id && (
                  <button
                    type="button"
                    style={{
                      ...buttonBaseStyle,
                      backgroundColor: '#1976D2',
                      color: '#fff',
                    }}
                    onClick={() => navigate(`/owner/venues/${id}/availability`)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#1565C0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#1976D2';
                    }}
                  >
                    <Calendar size={20} />
                    Configurar horarios
                  </button>
                )}
                {venuePhotos.length > 0 && createdVenueRef.current ? (
                  <button
                    type="button"
                    style={submitButtonStyle}
                    onClick={() => navigate('/owner/reservations', { state: { selectedVenueId: createdVenueRef.current.id } })}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#00D96F';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = theme.colors.primary;
                    }}
                  >
                    <Save size={20} />
                    Finalizar
                  </button>
                ) : (
                  <button
                    type="submit"
                    style={loading ? submitButtonDisabledStyle : submitButtonStyle}
                    disabled={loading}
                    onMouseEnter={(e) => {
                      if (!loading) {
                        e.currentTarget.style.backgroundColor = '#00D96F';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loading) {
                        e.currentTarget.style.backgroundColor = theme.colors.primary;
                      }
                    }}
                  >
                    <Save size={20} />
                    {loading
                      ? 'Guardando...'
                      : isEditing
                      ? 'Guardar cambios'
                      : 'Crear cancha'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Guide */}
          <div style={sidebarStyle}>
            <div style={guideCardStyle}>
              <h3
                style={{
                  ...sectionTitleStyle,
                  fontSize: theme.fonts.sizeBody,
                  marginBottom: '12px',
                }}
              >
                Consejos para tu publicación
              </h3>
              <div style={guideItemStyle}>
                ✓ Usá un nombre descriptivo y fácil de recordar
              </div>
              <div style={guideItemStyle}>
                ✓ Completá todos los datos de ubicación correctamente
              </div>
              <div style={guideItemStyle}>
                ✓ El link de Google Maps ayuda a los usuarios a encontrarte
              </div>
              <div style={guideItemStyle}>
                ✓ Marcá todas las comodidades que tengas disponibles
              </div>
              <div style={guideItemStyle}>
                ✓ Precio competitivo aumenta las reservas
              </div>
            </div>

            <div style={guideCardStyle}>
                <h3
                  style={{
                    ...sectionTitleStyle,
                    fontSize: theme.fonts.sizeBody,
                    marginBottom: '12px',
                  }}
                >
                  Consejos para fotos
                </h3>
                <div style={guideItemStyle}>
                  📸 Subí al menos 3-5 fotos de buena calidad
                </div>
                <div style={guideItemStyle}>
                  🌅 Tomá fotos con buena iluminación natural
                </div>
                <div style={guideItemStyle}>
                  📐 Mostrá diferentes ángulos de la cancha
                </div>
                <div style={guideItemStyle}>
                  🏟️ Incluí fotos de vestuarios y estacionamiento
                </div>
                <div style={guideItemStyle}>
                  🔄 Arrastrá las fotos para reordenarlas
                </div>
              </div>

            <div style={guideCardStyle}>
              <h3
                style={{
                  ...sectionTitleStyle,
                  fontSize: theme.fonts.sizeBody,
                  marginBottom: '12px',
                }}
              >
                Próximos pasos
              </h3>
              <div style={guideItemStyle}>
                1. Completá el formulario con los datos de tu cancha
              </div>
              <div style={guideItemStyle}>
                2. Subí fotos de calidad
              </div>
              <div style={guideItemStyle}>
                3. Configurá los horarios disponibles
              </div>
              <div style={guideItemStyle}>
                4. Tu cancha será revisada y publicada
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// Loading styles
const loadingContainerStyle: React.CSSProperties = {
  minHeight: '100vh',
  backgroundColor: theme.colors.background,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const loadingTextStyle: React.CSSProperties = {
  fontSize: theme.fonts.sizeSubtitle,
  color: theme.colors.textSecondary,
};

export default CreateVenuePage;
